import { useState } from "react";
import { MadeWithViny } from "../MadeWithViny";
import { Disc3, MapPin } from "lucide-react";
import { VerifiedBadge } from "@/components/ui/verified-badge";
import type { TemplateProps, AlbumItem } from "../types";
import { AlbumDetailDrawer } from "../AlbumDetailDrawer";

/* ─────────────────────────────────────────────────────────────
   Estúdio — headline gigante com selo [SELECTED WORKS], retrato
   no canto e grid editorial de capas numeradas.
   ───────────────────────────────────────────────────────────── */

function Cover({ a }: { a: AlbumItem }) {
  return a.cover_url ? (
    <img
      src={a.cover_url}
      alt={`${a.title} — ${a.artist}`}
      loading="lazy"
      decoding="async"
      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
    />
  ) : (
    <div className="grid h-full w-full place-items-center bg-neutral-200 text-neutral-400">
      <Disc3 className="h-1/3 w-1/3" />
    </div>
  );
}

function Grid({
  label,
  items,
  offset,
  onSelect,
}: {
  label: string;
  items: AlbumItem[];
  offset: number;
  onSelect: (a: AlbumItem) => void;
}) {
  if (!items.length) return null;
  return (
    <section className="mt-14 first:mt-0">
      <div className="flex items-baseline justify-between border-b border-neutral-900/10 pb-2">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-900">
          [{label}]
        </h2>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-x-5 gap-y-9 sm:grid-cols-3 lg:grid-cols-5">
        {items.slice(0, 60).map((a, i) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onSelect(a)}
            className="group text-left"
          >
            <div className="relative aspect-square overflow-hidden rounded-xl bg-neutral-200">
              <Cover a={a} />
            </div>
            <div className="mt-2.5 flex items-baseline gap-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] text-neutral-900">{a.title}</div>
                <div className="truncate font-mono text-[10px] uppercase tracking-[0.14em] text-neutral-500">
                  {a.artist}
                </div>
              </div>
              <span className="shrink-0 font-mono text-[11px] tabular-nums text-neutral-400">
                [{offset + i + 1}]
              </span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

export function EstudioTemplate({ bio, config }: TemplateProps) {
  const [selected, setSelected] = useState<AlbumItem | null>(null);
  const name = config.title || bio.display_name || bio.username;
  const tagline = config.subtitle || bio.bio;
  const collection = config.sections.collection !== false ? bio.owned : [];
  const wishlist = config.sections.wishlist !== false ? bio.wishlist : [];
  const place = [bio.city, bio.country].filter(Boolean).join(", ");
  const portrait =
    bio.avatar_url ||
    collection[0]?.cover_url ||
    `https://api.dicebear.com/7.x/initials/svg?seed=${bio.username}`;

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <div className="mx-auto max-w-[1600px] px-6 pb-16 pt-8 lg:px-12 lg:pb-20 lg:pt-10">
        {/* HEADLINE */}
        <header className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <h1
              className="flex flex-wrap items-center gap-x-5 gap-y-1 font-display font-normal leading-[0.88] tracking-tight"
              style={{ fontSize: "clamp(2.6rem, 7vw, 6.5rem)" }}
            >
              <span className="break-words">{name}</span>
              <span className="flex items-center gap-3">
                {bio.is_premium && (
                  <VerifiedBadge variant="shimmer" size={30} aria-label="Verificado" />
                )}
              </span>
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
              <span className="font-mono text-[11px] text-neutral-400">@{bio.username}</span>
              {place && (
                <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">
                  <MapPin className="h-3.5 w-3.5" /> {place}
                </span>
              )}
            </div>
          </div>

          <img
            src={portrait}
            alt={name}
            loading="lazy"
            decoding="async"
            className="hidden h-28 w-28 shrink-0 object-cover lg:block lg:h-36 lg:w-28"
          />
        </header>

        {/* BIO + BADGES */}
        <div className="mt-8 grid gap-6 border-t border-neutral-900/10 pt-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
          {tagline && (
            <p className="max-w-2xl text-[15px] leading-relaxed text-neutral-600">{tagline}</p>
          )}
          <div className="flex flex-wrap gap-1.5 lg:justify-end">
            {bio.favorite_genres.map((g) => (
              <span
                key={g}
                className="rounded-full border border-neutral-900/20 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-neutral-700"
              >
                {g}
              </span>
            ))}
          </div>
        </div>

        {/* GRIDS */}
        <main>
          {collection.length + wishlist.length === 0 ? (
            <div className="mt-14 grid place-items-center border border-dashed border-neutral-300 py-28 text-sm text-neutral-500">
              A coleção aparecerá aqui.
            </div>
          ) : (
            <>
              <Grid label="Coleção" items={collection} offset={0} onSelect={setSelected} />
              <Grid
                label="Lista de interesse"
                items={wishlist}
                offset={collection.length}
                onSelect={setSelected}
              />
            </>
          )}
        </main>
      </div>

      <footer className="flex items-center justify-between border-t border-neutral-900/10 px-6 py-5 font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500">
        <MadeWithViny />
        <span>{collection.length + wishlist.length} itens</span>
      </footer>

      <AlbumDetailDrawer
        album={selected}
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
      />
    </div>
  );
}
